/* ===============================================
   KICKBACK - Theme JavaScript
   Vanilla JS for animations, video modals, and interactions
   =============================================== */

(function() {
  'use strict';

  /**
   * Ref-counted page scroll lock. Avoids leaving body/html stuck on overflow:hidden
   * (that breaks position:fixed nav — looks like the header “falls into” a white page after closing a popup).
   * Alpine/promo modals may touch documentElement; we clear both when the last Kickback lock releases.
   */
  const kickbackBodyScrollLock = (() => {
    let count = 0;
    return {
      lock() {
        count += 1;
        if (count === 1) {
          document.body.style.overflow = 'hidden';
        }
      },
      unlock() {
        count = Math.max(0, count - 1);
        if (count === 0) {
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
        }
      },
    };
  })();

  /* ─── Scroll Reveal Animation ─── */
  const initScrollReveal = () => {
    const revealElements = document.querySelectorAll('.scroll-reveal');
    
    if (!revealElements.length) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          // Optional: unobserve after revealing (one-time animation)
          // observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    revealElements.forEach(el => observer.observe(el));
  };

  /* ─── Mobile Menu Toggle ─── */
  const initMobileMenu = () => {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const menuClose = document.querySelector('[data-mobile-menu-close]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    
    if (!menuToggle || !mobileMenu) return;
    
    const toggleMenu = (show) => {
      if (show) {
        mobileMenu.classList.add('is-open');
        kickbackBodyScrollLock.lock();
      } else {
        mobileMenu.classList.remove('is-open');
        kickbackBodyScrollLock.unlock();
      }
    };
    
    menuToggle.addEventListener('click', () => toggleMenu(true));
    
    if (menuClose) {
      menuClose.addEventListener('click', () => toggleMenu(false));
    }
    
    // Close on menu link click
    const menuLinks = mobileMenu.querySelectorAll('a');
    menuLinks.forEach(link => {
      link.addEventListener('click', () => toggleMenu(false));
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        toggleMenu(false);
      }
    });
  };

  /* ─── Header Cart Icon: open drawer instead of navigating, keep badge in sync ─── */
  const initCartIcon = () => {
    const cartLink = document.querySelector('.kickback-header-cart-icon');
    if (!cartLink) return;

    cartLink.addEventListener('click', (e) => {
      if (window.theme && window.theme.settings && window.theme.settings.cartType === 'drawer') {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
      }
    });

    const updateBadge = (count) => {
      const badge = document.querySelector('.kickback-header-cart-badge');
      const text = count > 99 ? '99+' : String(count);

      if (count > 0) {
        if (badge) {
          badge.textContent = text;
        } else {
          const newBadge = document.createElement('span');
          newBadge.className = 'kickback-header-cart-badge font-headline absolute flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full px-1 text-[10px] leading-none text-white';
          newBadge.style.cssText = 'background-color:#000000;top:0;right:0;transform:translate(15%,-15%);box-shadow:0 0 0 1px rgba(255,255,255,0.9);';
          newBadge.textContent = text;
          cartLink.appendChild(newBadge);
        }
      } else if (badge) {
        badge.remove();
      }
    };

    const parseCountFromSections = (data) => {
      const html = data && data.sections && data.sections['cart-item-count'];
      if (!html) return null;
      const count = parseInt(new DOMParser().parseFromString(html, 'text/html').body.textContent.trim(), 10);
      return Number.isNaN(count) ? null : count;
    };

    ['baseline:cart:afteradditem', 'baseline:cart:cartqtychange', 'baseline:cart:update'].forEach((eventName) => {
      window.addEventListener(eventName, (e) => {
        const count = parseCountFromSections(e.detail && e.detail.response);
        if (count !== null) updateBadge(count);
      });
    });
  };

  /* ─── Video Widget Modal ─── */
  const initVideoModals = () => {
    const videoTriggers = document.querySelectorAll('[data-video-trigger]');

    if (!videoTriggers.length) return;

    videoTriggers.forEach((trigger) => {
      if (trigger.dataset.kbVideoTriggerBound === '1') return;
      trigger.dataset.kbVideoTriggerBound = '1';

      trigger.addEventListener('click', () => {
        const videoSrc = trigger.dataset.videoSrc;
        const videoTitle = trigger.dataset.videoTitle || '';

        if (!videoSrc) return;

        /* One modal at a time; each open() did lock() — must unlock for every removed node or body stays scroll-locked. */
        const staleModals = document.querySelectorAll('.video-modal');
        const staleCount = staleModals.length;
        staleModals.forEach((el) => {
          el.classList.remove('is-open');
          el.remove();
        });
        for (let i = 0; i < staleCount; i += 1) {
          kickbackBodyScrollLock.unlock();
        }

        const modal = createVideoModal(videoSrc, videoTitle);
        document.body.appendChild(modal);
        kickbackBodyScrollLock.lock();

        requestAnimationFrame(() => {
          modal.classList.add('is-open');
        });

        const closeModal = () => {
          if (modal.dataset.kbVideoModalClosing === '1') return;
          modal.dataset.kbVideoModalClosing = '1';
          kickbackBodyScrollLock.unlock();
          modal.classList.remove('is-open');
          window.setTimeout(() => {
            modal.remove();
          }, 300);
        };

        const closeBtn = modal.querySelector('[data-modal-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            closeModal();
          });
        }

        modal.addEventListener('click', (e) => {
          const t = e.target;
          if (t === modal || (t && t.classList && t.classList.contains('video-modal__backdrop'))) {
            closeModal();
          }
        });

        document.addEventListener('keydown', function escHandler(e) {
          if (e.key === 'Escape') {
            closeModal();
            document.removeEventListener('keydown', escHandler);
          }
        });
      });
    });
  };

  const createVideoModal = (videoSrc, title) => {
    const modal = document.createElement('div');
    modal.className = 'video-modal';
    modal.innerHTML = `
      <div class="video-modal__backdrop"></div>
      <div class="video-modal__content">
        <video 
          src="${videoSrc}" 
          autoplay 
          loop 
          playsinline 
          controls
          class="video-modal__video"
        ></video>
        <button class="video-modal__close" data-modal-close aria-label="Close video">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;
    
    // Add modal styles if not already present
    if (!document.getElementById('video-modal-styles')) {
      const style = document.createElement('style');
      style.id = 'video-modal-styles';
      style.textContent = `
        .video-modal {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: max(1.5rem, var(--header-group-height, 72px)) 1.5rem 1.5rem;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .video-modal.is-open {
          opacity: 1;
        }
        .video-modal__backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.9);
        }
        .video-modal__content {
          position: relative;
          width: auto;
          max-width: min(92vw, 1200px);
          max-height: calc(100dvh - var(--header-group-height, 4.5rem) - 3rem);
          border-radius: 1rem;
          overflow: hidden;
          transform: scale(0.9);
          transition: transform 0.3s ease;
          line-height: 0;
        }
        .video-modal.is-open .video-modal__content {
          transform: scale(1);
        }
        .video-modal__video {
          display: block;
          width: auto;
          height: auto;
          max-width: min(92vw, 1200px);
          max-height: calc(100dvh - var(--header-group-height, 4.5rem) - 3rem);
          object-fit: contain;
          background: #000;
        }
        .video-modal__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .video-modal__close:hover {
          background: rgba(0, 0, 0, 0.8);
          border-color: rgba(255, 255, 255, 0.4);
        }
      `;
      document.head.appendChild(style);
    }
    
    return modal;
  };

  /* ─── Countdown Timer ─── */
  const initCountdowns = () => {
    const countdowns = document.querySelectorAll('[data-countdown]');
    
    if (!countdowns.length) return;
    
    countdowns.forEach(countdown => {
      const targetDate = new Date(countdown.dataset.countdown).getTime();
      
      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetDate - now;
        
        if (distance < 0) {
          countdown.innerHTML = '<span class="countdown-expired">Event Started!</span>';
          return;
        }
        
        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);
        
        const daysEl = countdown.querySelector('[data-countdown-days]');
        const hoursEl = countdown.querySelector('[data-countdown-hours]');
        const minutesEl = countdown.querySelector('[data-countdown-minutes]');
        const secondsEl = countdown.querySelector('[data-countdown-seconds]');
        
        if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      };
      
      updateCountdown();
      setInterval(updateCountdown, 1000);
    });
  };

  /* ─── Product Page: Image Gallery ─── */
  const initProductGallery = () => {
    const imageTriggers = document.querySelectorAll('[data-image-trigger]');
    const heroImages = document.querySelectorAll('.product-hero__image');
    
    if (!imageTriggers.length || !heroImages.length) return;
    
    imageTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const targetIndex = trigger.dataset.imageIndex;
        
        // Hide all images
        heroImages.forEach(img => {
          img.style.opacity = '0';
          img.classList.remove('is-active');
        });
        
        // Show target image
        const targetImage = document.querySelector(`[data-image-index="${targetIndex}"]`);
        if (targetImage) {
          targetImage.style.opacity = '1';
          targetImage.classList.add('is-active');
        }
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    });
  };

  /* ─── Product Page: Variant Selection ─── */
  const initVariantSelection = () => {
    const variantOptions = document.querySelectorAll('[data-variant-option]');
    const productForm = document.querySelector('[data-product-form]');
    
    if (!variantOptions.length || !productForm) return;
    
    variantOptions.forEach(option => {
      option.addEventListener('click', function(e) {
        e.preventDefault();
        
        const position = this.dataset.optionPosition;
        const value = this.dataset.value;
        
        // Update UI for this option group
        const siblingOptions = document.querySelectorAll(`[data-option-position="${position}"]`);
        siblingOptions.forEach(opt => {
          opt.setAttribute('aria-pressed', 'false');
          // Reset styles for swatches
          if (opt.classList.contains('variant-swatch') || opt.classList.contains('sticky-bar-swatch')) {
            opt.style.borderColor = 'rgba(0,0,0,0.15)';
            opt.style.transform = 'scale(1)';
          }
          // Reset styles for pills
          if (opt.classList.contains('variant-pill')) {
            opt.style.backgroundColor = 'transparent';
            opt.style.color = 'hsl(var(--foreground))';
            opt.style.borderColor = 'rgba(0,0,0,0.2)';
          }
        });
        
        // Set active state
        this.setAttribute('aria-pressed', 'true');
        if (this.classList.contains('variant-swatch') || this.classList.contains('sticky-bar-swatch')) {
          this.style.borderColor = 'hsl(var(--foreground))';
          this.style.transform = 'scale(1.1)';
        }
        if (this.classList.contains('variant-pill')) {
          this.style.backgroundColor = 'hsl(var(--foreground))';
          this.style.color = 'hsl(var(--background))';
          this.style.borderColor = 'hsl(var(--foreground))';
        }
        
        // Update selected value display
        const valueDisplay = this.closest('.variant-option-group').querySelector('[data-selected-value]');
        if (valueDisplay) {
          valueDisplay.textContent = value;
        }
        
        // Find matching variant and update form
        updateProductVariant();
      });
    });
    
    // Handle dropdowns
    const dropdowns = document.querySelectorAll('.variant-dropdown');
    dropdowns.forEach(dropdown => {
      dropdown.addEventListener('change', () => {
        updateProductVariant();
      });
    });
  };

  const updateProductVariant = () => {
    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return;
    
    const productData = window.productData;
    if (!productData) return;
    
    // Get selected options
    const selectedOptions = [];
    const optionGroups = document.querySelectorAll('.variant-option-group');
    
    optionGroups.forEach(group => {
      const position = group.dataset.optionPosition;
      const selectedButton = group.querySelector('[aria-pressed="true"]');
      const dropdown = group.querySelector('.variant-dropdown');
      
      if (selectedButton) {
        selectedOptions[position - 1] = selectedButton.dataset.value;
      } else if (dropdown) {
        selectedOptions[position - 1] = dropdown.value;
      }
    });
    
    // Find matching variant
    const matchingVariant = productData.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        return variant.options[index] === option;
      });
    });
    
    if (matchingVariant) {
      // Update hidden variant ID input
      const variantInput = productForm.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = matchingVariant.id;
      }
      
      // Update price displays
      const priceDisplays = document.querySelectorAll('[data-product-price]');
      priceDisplays.forEach(display => {
        display.textContent = formatMoney(matchingVariant.price);
      });
      
      // Update add to cart button
      const addToCartBtn = document.querySelector('[data-add-to-cart]');
      if (addToCartBtn) {
        if (matchingVariant.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'ADD TO CART';
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'SOLD OUT';
        }
      }
      
      // Update sticky bar button
      const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');
      if (stickyBtn) {
        stickyBtn.textContent = `${formatMoney(matchingVariant.price)} · ADD TO CART`;
      }
    }
  };

  const formatMoney = (cents) => {
    return '$' + (cents / 100).toFixed(2);
  };

  /* ─── Product Page: Quantity Selectors ─── */
  const initQuantitySelectors = () => {
    const decreaseBtns = document.querySelectorAll('[data-quantity-decrease]');
    const increaseBtns = document.querySelectorAll('[data-quantity-increase]');
    
    decreaseBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-quantity-input], [data-quantity-display]');
        const currentValue = parseInt(input.value || input.textContent);
        if (currentValue > 1) {
          if (input.tagName === 'INPUT') {
            input.value = currentValue - 1;
          } else {
            input.textContent = currentValue - 1;
          }
        }
      });
    });
    
    increaseBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-quantity-input], [data-quantity-display]');
        const currentValue = parseInt(input.value || input.textContent);
        if (input.tagName === 'INPUT') {
          input.value = currentValue + 1;
        } else {
          input.textContent = currentValue + 1;
        }
      });
    });
  };

  /* ─── Product Page: Sticky Buy Bar ─── */
  const initStickyBuyBar = () => {
    const stickyBar = document.querySelector('[data-sticky-buy-bar]');
    if (!stickyBar) return;
    
    let lastScroll = 0;
    const showThreshold = 600; // Show after scrolling 600px (reduced from 800px)
    
    const checkScroll = () => {
      const currentScroll = window.pageYOffset;
      
      if (currentScroll > showThreshold) {
        // Past threshold - show sticky bar
        stickyBar.classList.add('is-visible');
      } else {
        // Near top of page - hide sticky bar
        stickyBar.classList.remove('is-visible');
      }
      
      lastScroll = currentScroll;
    };
    
    window.addEventListener('scroll', checkScroll);
    // Check immediately on load
    checkScroll();
    
    // Sync quantity between main form and sticky bar
    const mainQuantity = document.querySelector('[data-product-form] [data-quantity-input]');
    const stickyQuantity = document.querySelector('[data-sticky-buy-bar] [data-quantity-display]');
    
    if (mainQuantity && stickyQuantity) {
      const syncQuantity = () => {
        stickyQuantity.textContent = mainQuantity.value;
      };
      
      mainQuantity.addEventListener('input', syncQuantity);
      
      // Also sync sticky bar quantity changes to main form
      const stickyDecrease = stickyBar.querySelector('[data-quantity-decrease]');
      const stickyIncrease = stickyBar.querySelector('[data-quantity-increase]');
      
      if (stickyDecrease) {
        stickyDecrease.addEventListener('click', () => {
          mainQuantity.value = stickyQuantity.textContent;
        });
      }
      
      if (stickyIncrease) {
        stickyIncrease.addEventListener('click', () => {
          mainQuantity.value = stickyQuantity.textContent;
        });
      }
    }
    
    // Handle sticky bar add to cart
    const stickyAddBtn = stickyBar.querySelector('[data-sticky-add-to-cart]');
    const mainForm = document.querySelector('[data-product-form]');
    
    if (stickyAddBtn && mainForm) {
      stickyAddBtn.addEventListener('click', () => {
        mainForm.requestSubmit();
        // Scroll to top to show cart notification
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  };

  /* ─── Product Page: Wishlist ─── */
  const initWishlist = () => {
    const wishlistBtns = document.querySelectorAll('[data-product-wishlist]');
    
    wishlistBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const svg = this.querySelector('svg');
        if (!svg) return;
        const currentFill = svg.getAttribute('fill');
        
        if (currentFill === 'currentColor') {
          svg.setAttribute('fill', 'none');
          // TODO: Remove from wishlist storage
        } else {
          svg.setAttribute('fill', 'currentColor');
          svg.style.color = '#e24a4a'; // Red
          // TODO: Add to wishlist storage
        }
      });
    });
  };

  /* ─── Waitlist Modal (shop rows, sold-out PDP tab, etc.) ─── */
  const closeWaitlistModal = (modal) => {
    kickbackBodyScrollLock.unlock();
    modal.classList.remove('show');
    modal.style.opacity = '0';
    const content = modal.querySelector('.waitlist-modal-content');
    if (content) {
      content.style.transform = 'translateY(20px)';
    }

    setTimeout(() => {
      modal.style.display = 'none';
    }, 300);
  };

  const findWaitlistModalEl = (modalId) => {
    if (!modalId) return null;
    let modal = document.getElementById(modalId);
    if (!modal && modalId.indexOf('waitlist-') === 0) {
      modal = document.getElementById(modalId.replace(/^waitlist-/, ''));
    }
    if (!modal) {
      try {
        const esc =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(modalId)
            : String(modalId).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        modal = document.querySelector('[data-waitlist-root-id="' + esc + '"]');
      } catch (e) {
        modal = null;
      }
    }
    return modal;
  };

  const showWaitlistModal = (modal) => {
    if (!modal) return;
    kickbackBodyScrollLock.lock();
    modal.style.display = 'flex';
    modal.classList.add('show');
    requestAnimationFrame(() => {
      modal.style.opacity = '1';
      const content = modal.querySelector('.waitlist-modal-content');
      if (content) {
        content.style.transform = 'translateY(0)';
      }
    });
  };

  /**
   * Shopify hCaptcha wires to customer/contact forms on load. We move waitlist popups to <body>
   * for correct fixed positioning — that reparenting breaks the default wiring and can cause
   * challenge redirects or broken submits. Re-protect after move / on open (see Shopify captcha docs).
   */
  const kickbackProtectShopifyCaptchaOnForm = (formEl) => {
    if (!formEl || formEl.tagName !== 'FORM') return;
    if (formEl.dataset.kbShopifyCaptchaWired === '1') return;
    const cap = window.Shopify && window.Shopify.captcha;
    if (!cap || typeof cap.protect !== 'function') return;
    try {
      cap.protect(formEl);
      formEl.dataset.kbShopifyCaptchaWired = '1';
    } catch (err) {
      /* Store has captcha off or storefront API missing — normal POST still works */
    }
  };

  const kickbackProtectWaitlistFormsInRoot = (root) => {
    if (!root) return;
    root.querySelectorAll('form.kickback-waitlist-shopify-form').forEach(kickbackProtectShopifyCaptchaOnForm);
  };

  /* ─── Shop waitlist overlay (waitlist-panel layout, [hidden] toggle) ─── */
  const initKickbackWaitlistPanelPopups = () => {
    /* Move popups under <body> so position:fixed is viewport-true (Shopify sections / scroll-reveal / transforms won’t trap fixed or clip the dimmer above the footer). */
    document.querySelectorAll('[data-kickback-wl-popup]').forEach((root) => {
      if (root.dataset.kbWlPortaled === '1') return;
      root.dataset.kbWlPortaled = '1';
      document.body.appendChild(root);
      kickbackProtectWaitlistFormsInRoot(root);
      window.setTimeout(() => kickbackProtectWaitlistFormsInRoot(root), 0);
    });

    if (document.documentElement.dataset.kbWlPopupDelegate === '1') return;
    document.documentElement.dataset.kbWlPopupDelegate = '1';

    document.addEventListener(
      'click',
      (e) => {
        const opener = e.target.closest('[data-kickback-wl-open]');
        if (!opener) return;
        const sel = opener.getAttribute('data-kickback-wl-target');
        if (!sel) return;
        e.preventDefault();
        e.stopPropagation();
        const root = document.querySelector(sel);
        if (root && root.hasAttribute('data-kickback-wl-popup')) {
          root.removeAttribute('hidden');
          kickbackBodyScrollLock.lock();
          kickbackProtectWaitlistFormsInRoot(root);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      (e) => {
        const closer = e.target.closest('[data-kickback-wl-close]');
        if (!closer) return;
        const root = closer.closest('[data-kickback-wl-popup]');
        if (!root) return;
        e.preventDefault();
        root.setAttribute('hidden', '');
        kickbackBodyScrollLock.unlock();
      },
      true
    );

    if (!window.__kickbackWlPopupEscapeBound) {
      window.__kickbackWlPopupEscapeBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openRoots = document.querySelectorAll('[data-kickback-wl-popup]:not([hidden])');
        const n = openRoots.length;
        if (!n) return;
        openRoots.forEach((r) => r.setAttribute('hidden', ''));
        for (let i = 0; i < n; i += 1) {
          kickbackBodyScrollLock.unlock();
        }
      });
    }
  };

  const initWaitlistModals = () => {
    document.querySelectorAll('[data-waitlist-modal]').forEach((modal) => {
      if (modal.dataset.kbWaitlistCloseBound === '1') return;
      modal.dataset.kbWaitlistCloseBound = '1';

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          closeWaitlistModal(modal);
        }
      });

      const closeBtn = modal.querySelector('.waitlist-close-btn');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          closeWaitlistModal(modal);
        });
      }
    });

    /* Bind only to the theme edge tab — not a document capture listener (that stole the first tap from app widgets in the same screen region). */
    document.querySelectorAll('.product-sold-out-waitlist-tab').forEach((trigger) => {
      if (trigger.dataset.kbWaitlistTabBound === '1') return;
      trigger.dataset.kbWaitlistTabBound = '1';
      trigger.addEventListener('click', (e) => {
        const modalId = trigger.getAttribute('data-modal-id') || trigger.dataset.modalId;
        if (!modalId) return;
        e.preventDefault();
        e.stopPropagation();
        showWaitlistModal(findWaitlistModalEl(modalId));
      });
    });

    if (!window.__kickbackWaitlistEscapeBound) {
      window.__kickbackWaitlistEscapeBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const openModal = document.querySelector('[data-waitlist-modal].show');
          if (openModal) {
            closeWaitlistModal(openModal);
          }
        }
      });
    }
  };

  /**
   * Cart drawer, promo bar, quick-buy, and app scripts often tweak html/body (overflow, classes).
   * That can make the top bar look “gone” for a frame. This watches those changes and forces the
   * header group back to visible + full opacity (matches kickback-chrome-stack.css).
   */
  const initHeaderChromeGuard = () => {
    const header = document.getElementById('kickback-header');
    if (!header || header.dataset.kbChromeGuard === '1') return;
    header.dataset.kbChromeGuard = '1';

    const punchKickbackChrome = () => {
      const bar = document.getElementById('kickback-header');
      if (!bar) return;
      bar.style.setProperty('opacity', '1', 'important');
      bar.style.setProperty('visibility', 'visible', 'important');
      bar.style.setProperty('animation', 'none', 'important');
      const group = document.querySelector('.shopify-section-group-header-group');
      if (group) {
        group.style.setProperty('visibility', 'visible', 'important');
      }
    };

    let chromeGuardRaf = 0;
    const scheduleChromePunch = () => {
      if (chromeGuardRaf) return;
      chromeGuardRaf = requestAnimationFrame(() => {
        chromeGuardRaf = 0;
        punchKickbackChrome();
      });
    };

    const observer = new MutationObserver(scheduleChromePunch);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    const modalRelatedEvents = [
      'cart-will-open',
      'cart-is-open',
      'cart-will-close',
      'cart-is-closed',
      'promo-is-open',
      'promo-is-closed',
      'modal-is-open',
      'modal-is-closed',
      'popup-is-open',
      'popup-is-closed',
    ];
    modalRelatedEvents.forEach((eventName) => {
      document.body.addEventListener(eventName, scheduleChromePunch);
      window.addEventListener(eventName, scheduleChromePunch);
    });

    /* Klaviyo and other apps append full-screen roots to body after load. */
    const bodyChildObserver = new MutationObserver(scheduleChromePunch);
    bodyChildObserver.observe(document.body, { childList: true, subtree: false });

    scheduleChromePunch();
  };

  /**
   * TEMPORARY: Klaviyo “onsite” stays enabled (reviews + tracking). This only dismisses sign-up
   * flyout/popup UI on product pages so it stops fighting the directory. Klaviyo documents hideForms()
   * for this pattern (e.g. when cart opens). Turn off by deleting this init or set KICKBACK_DISABLE_KLAVIYO_PDP_SUPPRESS.
   * Long-term: fix the form in Klaviyo (targeting / timing) instead of hiding in JS.
   */
  const initKlaviyoSignupSuppressOnPdp = () => {
    if (window.KICKBACK_DISABLE_KLAVIYO_PDP_SUPPRESS === true) return;
    const template = document.body && document.body.getAttribute('data-template');
    if (template !== 'product') return;

    const hideKlaviyoSignupForms = () => {
      const api = window.klaviyo || window.Klaviyo;
      if (!api || typeof api.hideForms !== 'function') return false;
      try {
        api.hideForms();
        return true;
      } catch (err) {
        return false;
      }
    };

    /* Script loads async — retry for a few seconds after load. */
    const startedAt = Date.now();
    const pollId = window.setInterval(() => {
      hideKlaviyoSignupForms();
      if (Date.now() - startedAt > 20000) window.clearInterval(pollId);
    }, 500);

    /* If Klaviyo opens a form after idle, close it again (PDP only). */
    window.addEventListener(
      'klaviyoForms',
      (e) => {
        const t = e && e.detail && e.detail.type;
        if (t === 'open' || t === 'embedOpen') hideKlaviyoSignupForms();
      },
      true
    );
  };

  const runInitSafe = (label, fn) => {
    try {
      fn();
    } catch (err) {
      console.error('[kickback-theme]', label, err);
    }
  };

  /**
   * CULT / PDP-style carousels: three identical sets in one row; on scroll we jump by ±setWidth at the
   * edges so the last card flows into the first with no hard stop. Same free horizontal scroll as Shop
   * product rows (no scroll-snap). Arrow buttons nudge by one card width + gap.
   */
  const KICKBACK_INFINITE_SETS = 3;
  const KICKBACK_INFINITE_EDGE = 2;

  const kickbackInfiniteCarouselStep = (carousel) => {
    const first = carousel.querySelector(':scope > .community-card, :scope > .review-card, :scope > .review-image-card');
    if (!first) return 0;
    const styles = window.getComputedStyle(carousel);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  };

  const kickbackBindInfiniteCarousel = (carousel) => {
    if (!carousel || carousel.dataset.kickbackInfiniteReady === '1') return;

    carousel.dataset.kickbackInfiniteReady = '1';

    let ignoreScroll = false;
    const setWidth = () => carousel.scrollWidth / KICKBACK_INFINITE_SETS;
    const maxScroll = () => Math.max(0, carousel.scrollWidth - carousel.clientWidth);

    const jumpToMiddleStart = () => {
      const sw = setWidth();
      if (sw < 1) return;
      ignoreScroll = true;
      carousel.scrollLeft = sw;
      requestAnimationFrame(() => {
        ignoreScroll = false;
      });
    };

    const onScroll = () => {
      if (ignoreScroll) return;
      const sw = setWidth();
      const maxS = maxScroll();
      if (sw < 1 || maxS < 1) return;

      const sl = carousel.scrollLeft;
      if (sl <= KICKBACK_INFINITE_EDGE) {
        ignoreScroll = true;
        carousel.scrollLeft = sl + sw;
        requestAnimationFrame(() => {
          ignoreScroll = false;
        });
      } else if (sl >= maxS - KICKBACK_INFINITE_EDGE) {
        ignoreScroll = true;
        carousel.scrollLeft = sl - sw;
        requestAnimationFrame(() => {
          ignoreScroll = false;
        });
      }
    };

    carousel.addEventListener('scroll', onScroll, { passive: true });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const navBehavior = reducedMotion ? 'auto' : 'smooth';

    const bindDir = (btn, dir) => {
      if (!btn || btn.dataset.kickbackInfiniteNavBound === '1') return;
      btn.dataset.kickbackInfiniteNavBound = '1';
      btn.addEventListener('click', () => {
        const step = kickbackInfiniteCarouselStep(carousel);
        if (step) carousel.scrollBy({ left: dir * step, behavior: navBehavior });
      });
    };

    const cid = carousel.id;
    if (cid) {
      document.querySelectorAll('[data-community-nav][data-carousel]').forEach((btn) => {
        if (btn.getAttribute('data-carousel') !== cid) return;
        const dir = btn.getAttribute('data-community-nav') === 'next' ? 1 : -1;
        bindDir(btn, dir);
      });
      document.querySelectorAll('[data-kickback-infinite-nav][data-kickback-infinite-target]').forEach((btn) => {
        if (btn.getAttribute('data-kickback-infinite-target') !== cid) return;
        const dir = btn.getAttribute('data-kickback-infinite-nav') === 'next' ? 1 : -1;
        bindDir(btn, dir);
      });
    }

    let resizeTimer;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(jumpToMiddleStart, 200);
    });
    ro.observe(carousel);

    window.setTimeout(jumpToMiddleStart, 80);
    window.setTimeout(jumpToMiddleStart, 450);
  };

  const initKickbackInfiniteCarousels = () => {
    document.querySelectorAll('[data-kickback-infinite-carousel]').forEach((el) => {
      if (el.dataset.kickbackInfiniteReady === '1') return;
      kickbackBindInfiniteCarousel(el);
    });
  };

  /** PDP hero: mobile-only button uses native share sheet when the browser supports it */
  const initProductHeroShare = () => {
    document.querySelectorAll('[data-product-hero-share]').forEach((btn) => {
      if (btn.dataset.productHeroShareBound === '1') return;
      if (!navigator.share) {
        btn.setAttribute('hidden', '');
        return;
      }
      btn.dataset.productHeroShareBound = '1';
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-share-url') || window.location.href;
        const title = btn.getAttribute('data-share-title') || document.title;
        try {
          await navigator.share({ title, text: title, url });
        } catch (err) {
          if (err && err.name === 'AbortError') return;
        }
      });
    });
  };

  window.kickbackOpenWaitlistModal = (modalId) => {
    showWaitlistModal(findWaitlistModalEl(String(modalId)));
  };

  /**
   * Fixed right-edge control with “notify” in the label — not our theme tab (.product-sold-out-waitlist-tab).
   */
  const kickbackFindFixedRightNotifyLauncher = () => {
    const candidates = document.querySelectorAll('button, a[href], [role="button"]');
    for (let i = 0; i < candidates.length; i += 1) {
      const node = candidates[i];
      if (node.closest('.kickback-sticky-buy-bar')) continue;
      if (node.matches('.product-sold-out-waitlist-tab')) continue;
      const text = (node.textContent || '').trim().toLowerCase();
      if (!text.includes('notify')) continue;
      const cs = window.getComputedStyle(node);
      if (cs.position !== 'fixed' && cs.position !== 'absolute') continue;
      const r = node.getBoundingClientRect();
      if (r.width < 8 || r.height < 8) continue;
      if (r.right >= window.innerWidth - 72 && r.left >= window.innerWidth - 96) {
        return node;
      }
    }
    return null;
  };

  /**
   * Open the same flow as the app’s sidebar Notify (programmatic .click() on its launcher).
   * @returns {boolean} true if a launcher was clicked
   */
  const kickbackTriggerAppNotifyLauncher = (selectorFromTheme) => {
    const tryClick = (el) => {
      if (!el || typeof el.click !== 'function') return false;
      try {
        el.click();
        return true;
      } catch (err) {
        return false;
      }
    };

    const custom =
      (typeof selectorFromTheme === 'string' && selectorFromTheme.trim()) ||
      (typeof window.KICKBACK_APP_NOTIFY_LAUNCHER_SELECTOR === 'string' && window.KICKBACK_APP_NOTIFY_LAUNCHER_SELECTOR.trim());
    if (custom) {
      try {
        const el = document.querySelector(custom);
        if (tryClick(el)) return true;
      } catch (err) {
        /* invalid selector */
      }
    }

    const trySelectors = [
      '[data-kickback-app-notify-launcher]',
      '#CartbiteWidget button',
      '#cartbite-widget button',
      '[class*="Cartbite" i] [class*="launcher" i]',
      '[class*="cartbite" i] button',
      '[id*="cartbite" i] button',
      '[class*="kbite" i] button',
      '[id*="kbite" i] button',
    ];

    for (let s = 0; s < trySelectors.length; s += 1) {
      try {
        const el = document.querySelector(trySelectors[s]);
        if (el && !el.closest('.kickback-sticky-buy-bar') && tryClick(el)) {
          return true;
        }
      } catch (err) {
        /* ignore */
      }
    }

    const edge = kickbackFindFixedRightNotifyLauncher();
    return tryClick(edge);
  };

  /**
   * Sticky sold-out CTA: trigger the app launcher (same popup as right edge). Fallback = theme modal.
   */
  const initKickbackStickyAppNotifyProxy = () => {
    document.querySelectorAll('[data-kickback-proxy-app-notify]').forEach((btn) => {
      if (btn.dataset.kbAppNotifyProxyBound === '1') return;
      btn.dataset.kbAppNotifyProxyBound = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const sel = btn.getAttribute('data-kickback-app-notify-selector') || '';
        const run = () => {
          const ok = kickbackTriggerAppNotifyLauncher(sel);
          if (!ok) {
            const fallbackId = btn.getAttribute('data-kickback-fallback-modal-id');
            if (fallbackId && window.kickbackOpenWaitlistModal) {
              window.kickbackOpenWaitlistModal(fallbackId);
            }
          }
        };
        window.requestAnimationFrame(() => {
          window.setTimeout(run, 0);
        });
      });
    });
  };

  /**
   * Plain PDP links (?open_waitlist=1 or #waitlist): prefer app launcher, else theme modal.
   */
  const initKickbackOpenWaitlistFromQuery = () => {
    const template = document.body && document.body.getAttribute('data-template');
    if (template !== 'product') return;
    const params = new URLSearchParams(window.location.search);
    const wantOpen = params.get('open_waitlist') === '1' || window.location.hash === '#waitlist';
    if (!wantOpen) return;
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        if (kickbackTriggerAppNotifyLauncher('')) {
          /* app */
        } else {
          let modalId = null;
          const tab = document.querySelector('.product-sold-out-waitlist-tab[data-modal-id]');
          if (tab) modalId = tab.getAttribute('data-modal-id');
          if (!modalId) {
            const m = document.querySelector('[data-waitlist-modal][id^="product-waitlist-"]');
            if (m && m.id) modalId = m.id;
          }
          if (!modalId) return;
          showWaitlistModal(findWaitlistModalEl(modalId));
        }
      }, 0);
    });
    if (params.get('open_waitlist') === '1') {
      params.delete('open_waitlist');
      const qs = params.toString();
      const cleanUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      history.replaceState({}, document.title, cleanUrl + (window.location.hash || ''));
    }
  };

  /**
   * After a native Shopify {% form 'customer' %} succeeds, Shopify redirects back with ?customer_posted=true.
   * Show a short toast and strip that param so the URL stays clean (footer newsletter + waitlist popups use this).
   */
  const initKickbackCustomerSignupSuccessToast = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') !== 'true') return;

    params.delete('customer_posted');
    const qs = params.toString();
    const cleanUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    history.replaceState({}, document.title, cleanUrl + window.location.hash);

    const toast = document.createElement('div');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = "You're on the list — thanks.";
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '1.25rem',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '10050',
      padding: '0.75rem 1.25rem',
      background: '#0a0a0a',
      color: '#fafafa',
      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
      fontSize: '11px',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      maxWidth: 'min(420px, calc(100vw - 2rem))',
      textAlign: 'center',
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      pointerEvents: 'none',
    });
    document.body.appendChild(toast);

    window.setTimeout(() => {
      toast.style.transition = 'opacity 0.4s ease';
      toast.style.opacity = '0';
      window.setTimeout(() => toast.remove(), 450);
    }, 5000);
  };

  /* ─── Initialize Everything on DOM Ready ─── */
  const init = () => {
    runInitSafe('initKickbackCustomerSignupSuccessToast', initKickbackCustomerSignupSuccessToast);
    runInitSafe('initHeaderChromeGuard', initHeaderChromeGuard);
    runInitSafe('initKlaviyoSignupSuppressOnPdp', initKlaviyoSignupSuppressOnPdp);
    initKickbackWaitlistPanelPopups();
    initWaitlistModals();
    initKickbackStickyAppNotifyProxy();
    initKickbackOpenWaitlistFromQuery();
    runInitSafe('initScrollReveal', initScrollReveal);
    runInitSafe('initMobileMenu', initMobileMenu);
    runInitSafe('initCartIcon', initCartIcon);
    runInitSafe('initVideoModals', initVideoModals);
    runInitSafe('initCountdowns', initCountdowns);
    runInitSafe('initProductGallery', initProductGallery);
    runInitSafe('initVariantSelection', initVariantSelection);
    runInitSafe('initQuantitySelectors', initQuantitySelectors);
    runInitSafe('initStickyBuyBar', initStickyBuyBar);
    runInitSafe('initWishlist', initWishlist);
    runInitSafe('initKickbackInfiniteCarousels', initKickbackInfiniteCarousels);
    runInitSafe('initProductHeroShare', initProductHeroShare);
  };

  // Initialize immediately if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  // Re-initialize on Shopify theme editor changes
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }
})();
